/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-case-declarations */
import React, { useState } from 'react'
import { Upload } from 'antd'
import { Paperclip } from 'react-feather'
import { BASE_URL } from '@pepper/utils/src/api/config'
import customFetch from '@pepper/utils/src/function/customFetch'

import {
  RcCustomRequestOptions,
  RcFile as AntRcFile,
  UploadFile as AntUploadFile
} from 'antd/lib/upload/interface'

import { Button } from '../../../atoms'
import { useShowMessage } from '../../../providers'

import styles from './_.module.css'

interface UploadFile extends AntUploadFile {
  id?: string | number
  flag?: boolean
  base64?: { file: string; name: string; type: string }
}

interface RcFile extends AntRcFile {
  flag?: boolean
}

// File upload component with base64 file conversion
export interface FileUploadProps {
  // css className on the container
  className?: string
  // enable multiple file uploading
  multipleUpload?: boolean
  // enable multiple file selection when uploading
  multipleSelection?: boolean
  // info text for upload
  info?: React.ReactElement | string
  // text on the button
  caption?: string
  // setting default files on component mount, send <file> object array containing {name, id, type}
  defaultFileList?: { name: string; id: string; type: string }[]
  // enable base64 upload, if enabled, base64 property containing {file: base64string, name, type} will also be sent in handleFiles
  base64?: boolean
  // maximum size of a single file in MBs
  maxFileSize?: number
  // extension that should be accepted for uploading
  allowedFiles?: string[]
  // text at end of the input
  suffix?: string
  //To disable the upload button
  disabled?: boolean
  // type of ui
  type?: 'default' | 'minimal'
  // limit the files number
  limit?: number | null
  files: any
  setFiles: (files: any) => void
}

const FileUpload = ({
  multipleUpload = false,
  multipleSelection = false,
  maxFileSize = 10,
  allowedFiles = ['.docx', '.pdf', '.png', '.jpeg', 'jpg'],
  disabled = false,
  limit = null,
  files = [],
  setFiles
}: FileUploadProps): React.ReactElement => {
  const url = `${BASE_URL}/files`

  const showMessage = useShowMessage()
  const [currFileBase64] = useState<any>(null)

  // set file.flag=true if the file has to be rejected by the list
  const beforeUpload = (file: RcFile, fileList: RcFile[]) => {
    /* regex to check file type */
    const regex = new RegExp(
      `([a-zA-Z0-9s_.-:()])+(${allowedFiles.join('|')})$`
    )

    /* check limit of file list if limit is set */
    if (limit && files.length >= limit) {
      showMessage({
        type: 'error',
        message: `Maximum ${limit} files are allowed`
      })
      file.flag = true
      return false
    }

    /* text file type */
    if (!regex.test(file.name.toLowerCase())) {
      showMessage({
        type: 'error',
        message: `Please upload files having extensions: ${allowedFiles.join(
          ', '
        )} only and ensure that the file name does not contain any unsupported characters.`
      })
      file.flag = true
      return false
    }

    /* check file size */
    const isSizeOkay = file.size / 1024 / 1024 < maxFileSize
    if (!isSizeOkay) {
      showMessage({
        type: 'error',
        message: `File must be smaller than ${maxFileSize}MB!`
      })
      file.flag = true
      return false
    }

    /* if all checks have passed */
    return true
  }

  const onChange = ({
    file,
    fileList
  }: {
    file: UploadFile
    fileList: UploadFile[]
  }) => {
    const { status, response, flag } = file

    // if file.flag is true then the file is rejected by beforeUpload function and update files list in parent component if corresponding function is supplied
    // https://github.com/ant-design/ant-design/issues/2423#issuecomment-233523579
    if (!flag && status !== 'removed') {
      if (multipleUpload) {
        const _fileList = fileList
        _fileList.forEach(_file => {
          if (!_file.id) {
            _file.id = _file.response
            _file.base64 = currFileBase64
          }
        })
        // Remove Invalid Files from list
        setFiles(fileList.filter(_file => !_file.flag))
      } else {
        file.id = response
        file.base64 = currFileBase64
        setFiles([file])
      }
    }

    switch (status) {
      case 'done':
        showMessage({
          type: 'success',
          message: 'File uploaded successfully.'
        })
        break
      case 'removed':
        const fileId = file.id
        setFiles(files.filter((_file: any) => fileId !== _file.id))
        break
      case 'error':
        showMessage({ type: 'error', message: response })
        break
      default:
        break
    }
  }

  const props = {
    accept: allowedFiles.join(),
    multiple: multipleSelection || false,
    beforeUpload,
    disabled: disabled || false,
    showUploadList: false,
    onChange,
    customRequest: async ({
      onSuccess,
      onError,
      file
    }: RcCustomRequestOptions): Promise<void> => {
      const res = await customFetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/octet-stream',
          'x-file-name': file.name
        },
        body: file
      })
      const response = await res.json()
      if (res.status === 200) {
        onSuccess(response.id, file)
      } else {
        onError(response.description)
      }
    }
  }

  return (
    <div className={styles.fileUploadContainer}>
      <Upload className={styles.uploadField} {...props} fileList={files}>
        <Button
          className={styles.uploadButton}
          icon={<Paperclip size={16} />}
          block={true}
        />
      </Upload>
    </div>
  )
}

export default FileUpload
